import axios from 'axios';
import {Auth} from "./aws";

const ApiService = axios.create({
  baseURL: '/',
  headers: {
    common: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  },
});

const ApiGet = (url) =>
  ApiService.get(url)
    .then(({data}) => data.data);
const ApiGetRaw = (url) =>
  ApiService.get(url)
    .then(({data}) => data);

const ApiGetCommon = (url) =>
  ApiService.get(url)
    .then(({data}) => data);

const ApiGetAdmin = (url) =>
  ApiService.get('/api/v1/admin' + url)
    .then(({data}) => data.data);

const ApiPost = (url, body) =>
  ApiService.post(url, body)
    .then(({data}) => data.data);

const ApiPostRaw = (url, body) =>
  ApiService.post(url, body)
    .then(({data}) => data);

const ApiPatch = (url, body) =>
  ApiService.patch(url, body)
    .then(({data}) => data);

const ApiDelete = (url, body = {}) =>
  ApiService.delete(url, {data: body})
    .then(({data}) => data);

const ApiPostAdmin = (url, body) =>
  ApiService.post('/api/v1/admin' + url, body)
    .then(({data}) => data);

const ApiPatchAdmin = (url, guid, body) =>
  ApiService.patch(`/api/v1/admin${url}/${guid}`, body)
    .then(({data}) => data);

const ApiDeleteAdmin = (url, guid, body) =>
  ApiService.delete(`/api/v1/admin${url}/${guid}`, body)
    .then(({data}) => data);

let refreshPromise = null;

const forceRefreshSession = () => {
  // Share a single refresh between concurrent 401s
  if (!refreshPromise) {
    refreshPromise = Promise.all([Auth.currentAuthenticatedUser(), Auth.currentSession()])
      .then(([user, session]) => new Promise((resolve, reject) => {
        user.refreshSession(session.getRefreshToken(), (err, newSession) => {
          if (err) reject(err)
          else resolve(newSession)
        })
      }))
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
};

ApiService.interceptors.request.use((request) => {
  if (request.url === '/api/v1/support-request' || request.url === '/api/v1/demo-request') return request
  return Auth.currentSession()
    .then(res => {
      request.headers['authorization'] = res.accessToken.jwtToken;
      return request
    })
});

/**
 * Add authorization header to request; API paths beginning with /a/ require authentication
 */

ApiService.interceptors.response.use((response) => {
  return response
}, (error) => {

  if (error === 'No current user') {
    location.href = '/login'
    return
  }

  let errorResponse = error.response

  if (errorResponse.status === 401 && errorResponse.config && !errorResponse.config.__isRetryRequest) {
    // Token may have expired server-side while still cached client-side; force a refresh and retry once
    errorResponse.config.__isRetryRequest = true;
    return forceRefreshSession()
      .then(() => ApiService(errorResponse.config))
      .catch(() => {
        location.href = '/login'
        return Promise.reject(errorResponse)
      })
  } else if (errorResponse.status === 401) {
    location.href = '/login'
  }

  return Promise.reject(errorResponse)

});

export async function promptAiText(promptInput) {
  const graphqlQuery = {
    query: `
      mutation promptAiText($input: PromptAiInput!) {
        result: promptAiText(input: $input)
      }
    `,
    variables: {
      input: promptInput,
    },
  };

  let authToken = (await Auth.currentSession()).idToken.jwtToken

  let response;

  try {
    response = await fetch('https://eis-api.departmentware.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${authToken}`,
      },
      body: JSON.stringify(graphqlQuery),
    });
  } catch (error) {
    const requestError = new Error('Could not connect to the AI service.');
    requestError.details = error.stack || error.message;
    throw requestError;
  }

  let responseBody;

  try {
    responseBody = await response.json();
  } catch (error) {
    const responseError = new Error('The AI service returned an unreadable response.');
    responseError.details = `HTTP ${response.status} ${response.statusText || ''}\n${error.message}`.trim();
    throw responseError;
  }

  if (!response.ok) {
    const responseError = new Error('The AI service could not process the request.');
    responseError.details = JSON.stringify({
      status: response.status,
      statusText: response.statusText,
      response: responseBody,
    }, null, 2);
    throw responseError;
  }

  if (responseBody.errors && responseBody.errors.length) {
    const responseError = new Error('The AI service returned an error.');
    responseError.details = JSON.stringify({errors: responseBody.errors}, null, 2);
    throw responseError;
  }

  return responseBody;
}

export default ApiService;

export {
  ApiService,
  ApiGet,
  ApiGetRaw,
  ApiPost,
  ApiPostRaw,
  ApiGetAdmin,
  ApiPostAdmin,
  ApiPatchAdmin,
  ApiDeleteAdmin,
  ApiGetCommon,
  ApiPatch,
  ApiDelete
}
